import { useState, useRef, KeyboardEvent } from 'react';
import { Send, Image as ImageIcon, X, Loader2 } from 'lucide-react';

interface MessageInputProps {
  onSendMessage: (content: string) => Promise<void>;
  onSendImage: (file: File) => Promise<void>;
  isSending: boolean;
  disabled?: boolean;
}

export function MessageInput({
  onSendMessage,
  onSendImage,
  isSending,
  disabled = false,
}: MessageInputProps) {
  const [message, setMessage] = useState('');
  const [selectedImage, setSelectedImage] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleSend = async () => {
    if (isSending || disabled) return;

    if (selectedImage) {
      await onSendImage(selectedImage);
      clearImage();
    } else if (message.trim()) {
      const messageToSend = message.trim();
      setMessage('');
      // Reset textarea height
      if (textareaRef.current) {
        textareaRef.current.style.height = 'auto';
      }
      try {
        await onSendMessage(messageToSend);
      } catch {
        // Restore input if send fails so user can retry.
        setMessage(messageToSend);
        requestAnimationFrame(() => {
          if (textareaRef.current) {
            textareaRef.current.style.height = 'auto';
            textareaRef.current.style.height = `${Math.min(textareaRef.current.scrollHeight, 120)}px`;
          }
        });
      }
    }
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    // Cmd+Enter (Mac) or Ctrl+Enter (Windows/Linux) で送信
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      handleSend();
    }
    // 通常のEnterは改行（デフォルト動作なので何もしない）
  };

  const handleTextareaChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setMessage(e.target.value);
    // Auto-resize textarea
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = `${Math.min(textareaRef.current.scrollHeight, 120)}px`;
    }
  };

  const handleImageSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        alert('画像ファイルを選択してください');
        return;
      }
      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        alert('画像サイズは5MB以下にしてください');
        return;
      }

      setSelectedImage(file);
      const reader = new FileReader();
      reader.onload = (e) => {
        setImagePreview(e.target?.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const clearImage = () => {
    setSelectedImage(null);
    setImagePreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const canSend = !disabled && !isSending && (message.trim() || selectedImage);

  return (
    <div className="border-t border-[#EDEBE6] bg-white">
      {/* Image preview */}
      {imagePreview && (
        <div className="p-3 pb-1">
          <div className="relative inline-block">
            <img
              src={imagePreview}
              alt="選択した画像"
              className="max-h-32 rounded-2xl object-contain"
            />
            <button
              onClick={clearImage}
              className="absolute -right-2 -top-2 flex h-6 w-6 items-center justify-center rounded-full bg-neutral-800 text-white transition hover:bg-neutral-700"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {/* Input area */}
      <div className="flex items-center gap-2 p-3">
        {/* Image upload button */}
        <button
          onClick={() => fileInputRef.current?.click()}
          disabled={disabled || isSending}
          className="flex h-8 w-8 shrink-0 items-center justify-center text-[#C0BDB8] transition hover:text-[#A99986] disabled:cursor-not-allowed disabled:opacity-50"
        >
          <ImageIcon className="h-[19px] w-[19px]" />
        </button>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          onChange={handleImageSelect}
          className="hidden"
        />

        {/* Text input */}
        <div className="flex min-h-[38px] flex-1 items-center rounded-full bg-[#F5F2ED] px-4">
          <textarea
            ref={textareaRef}
            value={message}
            onChange={handleTextareaChange}
            onKeyDown={handleKeyDown}
            placeholder="メッセージを入力..."
            rows={1}
            disabled={disabled || isSending || !!selectedImage}
            className="w-full resize-none bg-transparent py-2 text-[14px] leading-5 text-[#1A1A1A] placeholder-[#C0BDB8] outline-none disabled:cursor-not-allowed"
            style={{ maxHeight: '120px' }}
          />
        </div>

        {/* Send button */}
        <button
          onClick={handleSend}
          disabled={!canSend}
          className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full transition-all ${
            canSend ? 'bg-[#B87333]' : 'bg-[#ECEAE6]'
          }`}
          style={canSend ? { boxShadow: '0 2px 8px rgba(184,115,51,0.32)' } : undefined}
        >
          {isSending ? (
            <Loader2 className="h-[18px] w-[18px] animate-spin text-white" />
          ) : (
            <Send size={15} color={canSend ? 'white' : '#C0BDB8'} />
          )}
        </button>
      </div>
    </div>
  );
}
